// components/AwsPricingImport.jsx — tab Import: tabel proposal → baris cart.
//
// DUA JALUR MASUK, SATU JALUR KELUAR.
//   gambar  → POST /api/aws/pricing/import/extract   (Bedrock membaca, lalu validasi)
//   Excel/CSV → SheetJS di klien → POST /api/aws/pricing/import/validate (tanpa AI)
// Keduanya menghasilkan pasangan `candidates` + `rows` dengan panjang sama dan
// indeks sejajar; sisa komponen ini tidak peduli mana asalnya.
//
// KENAPA EXCEL TIDAK LEWAT MODEL. Kolom spreadsheet sudah terpisah; menyuruh
// model membacanya berarti membayar token untuk menambahkan ketidakpastian pada
// data yang sudah deterministik. Yang tersisa hanyalah pemetaan kolom, dan itu
// dipilih manusia sekali lalu terlihat.
//
// ── BATAS YANG TIDAK BISA DITUTUP ───────────────────────────────────────────
// Salah BACA tetap mungkin dan tidak terdeteksi. 'm6i.4xlarge' yang terbaca
// 'm6i.xlarge' menghasilkan baris HIJAU dengan harga ~4x lebih kecil — kedua
// tipe sah di katalog, jadi validator tidak punya dasar menolak.
// Karena itu `source_text` (kutipan mentah dari sumber) ditampilkan
// BERDAMPINGAN dengan spec hasil resolusi, bukan disembunyikan di balik tooltip
// atau mode lanjutan. Perbandingan itu satu-satunya pertahanan yang ada, dan
// pertahanan yang harus diklik tidak akan pernah dipakai.
//
// ── KATALOG YANG MEMUTUSKAN, BUKAN KLIEN ────────────────────────────────────
// Saat pengguna mengisi field `needs`, komponen ini TIDAK menambal `spec` yang
// sudah ada. Ia menulis ulang kandidatnya lalu memanggil /import/validate untuk
// baris itu saja. Alasannya kaskade RDS: memilih db_engine mengubah pilihan
// deployment_option yang sah, lalu db_edition dan license_model. Menambal di
// klien akan menghasilkan kombinasi yang tampak lengkap tetapi tidak ada di
// katalog — persis kelas kesalahan yang tidak terlihat dari angkanya.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// xlsx-js-style, BUKAN 'xlsx'. Keduanya SheetJS dan keduanya ada di
// dependencies, tetapi paketnya berbeda — mengimpor 'xlsx' di sini menaruh
// SALINAN KEDUA SheetJS di main.js, karena AwsPricingExport.jsx sudah memuat
// fork ini. Fork itu superset: XLSX.read dan sheet_to_json identik, dan
// komponen ini hanya membaca.
import * as XLSX from 'xlsx-js-style';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import {
  Upload, FileSpreadsheet, Image as ImageIcon, X, CheckCircle2, AlertTriangle,
  XCircle, Loader2, ShoppingCart, Trash2, Info,
} from 'lucide-react';
import SearchableSelect from './SearchableSelect';

const REGIONS = [
  { code: 'ap-southeast-3', label: 'Jakarta (ap-southeast-3)' },
  { code: 'ap-southeast-1', label: 'Singapore (ap-southeast-1)' },
];

// Batas yang SAMA dengan route /import/extract. Diperiksa di klien supaya
// penolakan terjadi sebelum 12 MB base64 dikirim melewati jaringan, bukan
// sesudahnya. Backend tetap memeriksanya sendiri — ini bukan penggantinya.
const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 4 * 1024 * 1024;
const IMAGE_MIME = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

const SHEET_EXT = /\.(xlsx|xlsm|xls|csv)$/i;

// Gambar dari clipboard umumnya bernama kosong atau generik ('image.png'), jadi
// dua tempelan berbeda akan tampak sebagai berkas yang sama di daftar. Nama
// bercap waktu membuat keduanya bisa dibedakan tanpa pengguna menebak mana yang
// mana.
const pasteName = (mime, n) => {
  const ext = (String(mime || '').split('/')[1] || 'png').replace('jpeg', 'jpg');
  const d = new Date(); const p = (x) => String(x).padStart(2, '0');
  return `paste-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}${n > 1 ? `-${n}` : ''}.${ext}`;
};

// Field kandidat yang bisa dipetakan dari kolom spreadsheet. Sengaja hanya
// field DESKRIPTIF: usage_type, operation, sku, dan harga apa pun diresolusi di
// backend dari katalog dan tidak bisa dititipkan lewat berkas.
const SHEET_FIELDS = [
  { key: 'kind', label: 'kind (ec2 / rds / opensearch / usage)' },
  { key: 'source_text', label: 'source_text (kutipan baris asli)' },
  { key: 'instance_type', label: 'instance_type' },
  { key: 'os', label: 'os' },
  { key: 'tenancy', label: 'tenancy' },
  { key: 'pre_installed_sw', label: 'pre_installed_sw' },
  { key: 'db_engine', label: 'db_engine' },
  { key: 'db_edition', label: 'db_edition' },
  { key: 'deployment_option', label: 'deployment_option' },
  { key: 'license_model', label: 'license_model' },
  { key: 'usage_key', label: 'usage_key (untuk kind=usage)' },
  { key: 'amount', label: 'amount (jumlah pemakaian)' },
  { key: 'qty', label: 'qty' },
];

// Tebakan awal pemetaan dari nama header. Ini KENYAMANAN, bukan keputusan:
// hasilnya tampil di dropdown dan bisa diubah sebelum apa pun dikirim.
const HEADER_HINTS = [
  [/^(kind|jenis|tipe.?baris|type)$/i, 'kind'],
  [/(instance.?type|tipe.?instance|shape)/i, 'instance_type'],
  [/^(os|sistem.?operasi|operating)/i, 'os'],
  [/tenancy/i, 'tenancy'],
  [/(pre.?installed|software)/i, 'pre_installed_sw'],
  [/(db.?engine|engine|mesin.?db)/i, 'db_engine'],
  [/(db.?edition|edition|edisi)/i, 'db_edition'],
  [/(deployment|multi.?az|az.?mode)/i, 'deployment_option'],
  [/(license|lisensi)/i, 'license_model'],
  [/(usage.?key|kunci.?pemakaian)/i, 'usage_key'],
  [/(amount|jumlah|volume|size|ukuran|gb|qty.?gb)/i, 'amount'],
  [/^(qty|quantity|jumlah.?unit|unit|count|node)/i, 'qty'],
  [/(source|keterangan|deskripsi|description|item|komponen|spesifikasi)/i, 'source_text'],
];

// Baris header dicari dengan MENCOCOKKAN nama field, bukan dengan mengambil
// baris tak-kosong pertama.
//
// Alasannya empiris: lembar Re-import punya judul dan catatan di atas tabelnya,
// dan tabel proposal pihak ketiga hampir selalu begitu juga. Mengambil baris
// pertama menggeser SELURUH kolom — uji round-trip 03-09-2026 menghasilkan 0
// dari 13 kolom terpetakan dan setiap kind bernilai null. Kegagalannya total,
// jadi terlihat; yang berbahaya adalah variannya di mana hanya sebagian kolom
// bergeser.
//
// Baris dengan kecocokan TERBANYAK yang menang, minimal dua — satu kecocokan
// bisa datang dari sel prosa yang kebetulan memuat kata 'unit'. Bila tidak ada
// yang memenuhi, kembali ke perilaku lama supaya berkas tanpa header yang bisa
// dikenali tetap bisa dipetakan manual.
const scoreHeaderRow = (row) => {
  const taken = new Set();
  let n = 0;
  (row || []).forEach(h => {
    const name = String(h || '').trim();
    if (!name) return;
    for (const [re, field] of HEADER_HINTS) {
      if (taken.has(field)) continue;
      if (re.test(name)) { taken.add(field); n += 1; break; }
    }
  });
  return n;
};

const findHeaderRow = (aoa) => {
  let best = -1, bestScore = 1;
  // Hanya 20 baris teratas diperiksa: header yang lebih dalam dari itu bukan
  // tabel lagi, dan memindai seluruh lembar membuat baris data yang kebetulan
  // berisi kata kunci bisa mengalahkan header sebenarnya.
  for (let i = 0; i < Math.min(aoa.length, 20); i += 1) {
    const sc = scoreHeaderRow(aoa[i]);
    if (sc > bestScore) { bestScore = sc; best = i; }
  }
  if (best >= 0) return best;
  return aoa.findIndex(r => (r || []).some(c => String(c || '').trim() !== ''));
};

const guessMapping = (headers) => {
  const map = {};
  const taken = new Set();
  headers.forEach((h, i) => {
    const name = String(h || '').trim();
    if (!name) return;
    for (const [re, field] of HEADER_HINTS) {
      if (taken.has(field)) continue;
      if (re.test(name)) { map[field] = i; taken.add(field); break; }
    }
  });
  return map;
};

// Angka dari sel spreadsheet. TIDAK menafsirkan satuan: '5 TB' menghasilkan
// null, bukan 5120. Konversi satuan adalah penafsiran, dan penafsiran yang
// benar untuk satu berkas belum tentu benar untuk berkas berikutnya. Baris
// tanpa angka yang jelas menjadi kuning dengan `needs: ['amount']` dan diisi
// manusia — lebih lambat, tetapi tidak pernah salah 1024x.
const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  // Nol dan negatif ditolak di KEDUA cabang. Sebelumnya sel numerik 0 lolos
  // sementara teks '0' ditolak — cabang yang menyimpang seperti itu berarti
  // hasilnya bergantung pada apakah XLSX membaca sel sebagai angka atau teks,
  // hal yang tidak terlihat dari isi berkasnya.
  if (typeof v === 'number') return (Number.isFinite(v) && v > 0) ? v : null;
  const raw = String(v).trim();
  // Koma HANYA diterima sebagai pemisah ribuan dalam kelompok tiga digit
  // ('1,024' → 1024). '2,5' DITOLAK, bukan dibaca 25.
  //
  // Ini bukan kehati-hatian berlebihan: koma desimal adalah notasi baku
  // Indonesia, tabel proposal ditulis di Indonesia, dan menghapus koma begitu
  // saja mengubah 2,5 TB menjadi 25 — galat 10x yang keluar sebagai baris
  // HIJAU karena 25 adalah angka yang sah. Angka bermakna ganda harus jadi
  // pertanyaan ke manusia, bukan tafsiran.
  const s = /^[0-9]{1,3}(,[0-9]{3})+(\.[0-9]+)?$/.test(raw)
    ? raw.replace(/,/g, '')
    : raw;
  if (!/^[0-9]+(\.[0-9]+)?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const cell = (row, idx) => (idx === undefined || idx === null || idx < 0
  ? undefined
  : (row[idx] === undefined || row[idx] === null || String(row[idx]).trim() === ''
      ? undefined : String(row[idx]).trim()));

// Analisis satu lembar: baris header, isi tabel, dan pemetaan kolom hasil
// tebakan. Dipisah dari applySheet() karena dipakai untuk DUA hal — memilih
// lembar mana yang dibuka, dan menampilkan skor setiap lembar di dropdown.
const analyzeSheet = (book, name) => {
  const ws = book.Sheets[name];
  // header:1 memberi array-of-array. Objek berkunci header tidak dipakai
  // karena header duplikat ('Qty' dua kali) akan saling menimpa tanpa
  // peringatan, dan tabel proposal sering punya kolom bernama sama.
  const aoa = ws ? XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' }) : [];
  const headerIdx = findHeaderRow(aoa);
  const headers = headerIdx >= 0 ? (aoa[headerIdx] || []).map(c => String(c || '').trim()) : [];
  const body = headerIdx >= 0 ? aoa.slice(headerIdx + 1) : [];
  const mapping = guessMapping(headers);
  return { headers, body, mapping, mapped: Object.keys(mapping).length };
};

// Lembar yang dibuka adalah yang paling banyak kolomnya dikenali — BUKAN
// SheetNames[0].
//
// Lembar pertama adalah tebakan yang hampir selalu salah untuk berkas yang
// justru punya lembar data khusus. Diukur pada berkas ekspor nyata
// (03-09-2026): 'Cost Comparison Report' 2 kolom terpetakan, 'Assumptions' 2,
// 'Re-import' 13. Selisihnya tidak tipis, jadi pemilihannya deterministik.
//
// Yang membuat kekeliruan lama sulit dilihat: 'Assumptions' menghasilkan 9
// kandidat — kebetulan sama dengan jumlah baris yang benar — dan kolom Item-nya
// terisi. Jumlah kandidat yang tampak wajar BUKAN tanda lembar yang benar;
// hanya jumlah kolom terpetakan yang membedakannya. Karena itu skornya
// ditampilkan di UI, bukan hanya dipakai diam-diam.
const pickBestSheet = (book) => {
  const scores = {};
  let best = book.SheetNames[0] || null;
  let bestScore = -1;
  book.SheetNames.forEach(n => {
    const a = analyzeSheet(book, n);
    scores[n] = a.mapped;
    // '>' bukan '>=': pada skor seri, lembar TERDEPAN menang. Itu perilaku lama
    // dan tetap benar untuk berkas satu-lembar.
    if (a.mapped > bestScore) { bestScore = a.mapped; best = n; }
  });
  return { best, scores };
};

// Di bawah ambang ini pemetaan otomatis praktis tidak menemukan apa pun, dan
// menekan Validate hanya akan menghasilkan barisan merah. Angkanya bukan
// keramat: 2 adalah skor yang dicapai lembar laporan dan lembar Assumptions
// pada berkas ekspor nyata, jadi ambang 3 memisahkan keduanya dari lembar data
// sungguhan tanpa menghalangi berkas sederhana berkolom sedikit.
const LOW_MAPPING = 3;

const STATUS_STYLE = {
  green: { box: 'border-emerald-300 bg-emerald-50', chip: 'bg-emerald-600', Icon: CheckCircle2, text: 'text-emerald-800' },
  yellow: { box: 'border-amber-300 bg-amber-50', chip: 'bg-amber-500', Icon: AlertTriangle, text: 'text-amber-900' },
  red: { box: 'border-red-300 bg-red-50', chip: 'bg-red-600', Icon: XCircle, text: 'text-red-800' },
};

// Baris boleh masuk cart hanya bila TIDAK ada `needs`. `defaults` tidak
// menghalangi — nilainya punya dasar (Shared / NA) dan tetap bertanda di UI.
//
// Penjaga ini bukan kosmetik untuk 'operation': fetchTermsByUsageType() hanya
// memasang penyaring operation bila nilainya truthy, jadi baris usage yang
// lolos tanpa operation terpilih akan mengambil tarif SELURUH operation pada
// usage_type itu dan memperlakukannya sebagai tier dari satu produk. Angkanya
// keluar tanpa galat, dan salah.
const isReady = (r) => !!r && r.status !== 'red' && !(r.needs || []).length;

// Field yang ditampilkan pada ringkasan spec, per kind. Urutannya tetap supaya
// dua baris bisa dibandingkan sekilas tanpa membaca nama field.
const SPEC_FIELDS = {
  ec2: ['instance_type', 'os', 'tenancy', 'pre_installed_sw', 'qty'],
  rds: ['instance_type', 'db_engine', 'db_edition', 'deployment_option', 'license_model', 'qty'],
  opensearch: ['instance_type', 'qty'],
  usage: ['service_code', 'usage_type', 'operation', 'amount', 'qty'],
};

export default function AwsPricingImport({ region: initialRegion, onSendToCart }) {
  const { t } = useLang();
  const tt = useCallback((key, fb) => {
    try { const v = t(key); return (v && v !== key) ? v : fb; } catch (_) { return fb; }
  }, [t]);

  const [region, setRegion] = useState(initialRegion || 'ap-southeast-3');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Kandidat & hasil validasi selalu sejajar indeks — validateCandidates()
  // memetakan satu-ke-satu. Menyimpan keduanya wajib: revalidasi memerlukan
  // kandidat aslinya, panel review memerlukan bacaan mentahnya.
  const [cands, setCands] = useState([]);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [rowBusy, setRowBusy] = useState({});

  const [usageKeys, setUsageKeys] = useState([]);
  const [unsupported, setUnsupported] = useState([]);

  // ── Berkas ──────────────────────────────────────────────────────────────
  const [images, setImages] = useState([]);     // { name, size, mimetype, data_base64 }
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  // addImageFiles dibaca lewat ref, BUKAN lewat deps efek di bawah.
  //
  // Fungsi itu menutup atas `images` untuk menghitung sisa kuota, jadi versi
  // yang tertangkap saat listener dipasang akan selalu melihat daftar kosong —
  // tempelan kedua dan seterusnya akan menimpa hitungan, bukan menambah.
  // Memasukkannya ke deps akan melepas-pasang listener setiap render.
  const addImagesRef = useRef(null);

  // ── Spreadsheet ─────────────────────────────────────────────────────────
  const [sheet, setSheet] = useState(null);     // { fileName, sheetNames, active, headers, body }
  const [mapping, setMapping] = useState({});
  const [fixedKind, setFixedKind] = useState('');
  const [wb, setWb] = useState(null);

  useEffect(() => {
    let dead = false;
    api.get('/api/aws/pricing/import/keys')
      .then(res => {
        if (dead) return;
        setUsageKeys(res.data?.usage_keys || []);
        setUnsupported(res.data?.unsupported || []);
      })
      .catch(e => { if (!dead) setError(e?.response?.data?.error || e.message); });
    return () => { dead = true; };
  }, []);

  const resetResults = () => { setCands([]); setRows([]); setMeta(null); setNotice(null); };

  // ── Tempel gambar (Ctrl+V) ──────────────────────────────────────────────
  //
  // Listener dipasang di `document` supaya menempel bekerja tanpa mengklik apa
  // pun lebih dulu. Screenshot tabel proposal hampir selalu lahir dari
  // Win+Shift+S dan berakhir di clipboard, jadi memaksa simpan-ke-berkas dulu
  // menambah dua langkah yang tidak perlu ada.
  //
  // PENJAGA: paste yang terjadi di dalam input atau textarea dilewatkan. Satu
  // satunya kotak isian di tab ini adalah angka untuk field `needs`, dan
  // membajak paste di sana akan merusak hal yang jelas berguna demi hal yang
  // tidak berarti (menempel gambar ke kotak angka).
  useEffect(() => {
    const onPaste = (e) => {
      const tag = String(e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      const items = [...(e.clipboardData?.items || [])];
      const files = [];
      items.forEach((it) => {
        if (it.kind !== 'file' || !String(it.type || '').startsWith('image/')) return;
        const f = it.getAsFile();
        if (!f) return;
        // File dari clipboard sering bernama kosong; diganti supaya daftarnya
        // bisa dibaca. Tipe dan ukurannya tetap milik berkas aslinya, jadi
        // validasi format dan 4 MB di addImageFiles berlaku apa adanya.
        files.push(new File([f], pasteName(f.type, files.length + 1), { type: f.type }));
      });
      if (!files.length) return;
      // preventDefault hanya setelah ADA gambar yang diambil, supaya menempel
      // teks di halaman ini tidak ikut diblokir.
      e.preventDefault();
      addImagesRef.current?.(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, []);

  // ── Gambar ──────────────────────────────────────────────────────────────

  const readAsBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || '');
      const comma = s.indexOf(',');
      resolve(comma > 0 ? s.slice(comma + 1) : s);
    };
    r.onerror = () => reject(new Error(`Gagal membaca ${file.name}.`));
    r.readAsDataURL(file);
  });

  const addImageFiles = async (files) => {
    setError(null);
    const accepted = [];
    const rejected = [];
    for (const f of files) {
      if (!IMAGE_MIME.includes(f.type)) { rejected.push(`${f.name} — format ${f.type || 'tak dikenal'} tidak didukung`); continue; }
      if (f.size > MAX_IMAGE_BYTES) { rejected.push(`${f.name} — ${(f.size / 1048576).toFixed(1)} MB melewati batas 4 MB`); continue; }
      accepted.push(f);
    }
    const room = MAX_IMAGES - images.length;
    if (accepted.length > room) {
      rejected.push(`${accepted.length - room} berkas diabaikan — maksimum ${MAX_IMAGES} gambar per permintaan`);
      accepted.length = Math.max(room, 0);
    }
    try {
      const loaded = await Promise.all(accepted.map(async f => ({
        name: f.name, size: f.size, mimetype: f.type,
        data_base64: await readAsBase64(f),
      })));
      if (loaded.length) setImages(prev => [...prev, ...loaded]);
    } catch (e) { rejected.push(e.message); }
    setNotice(rejected.length ? rejected.join(' · ') : null);
  };
  // Diisi ulang setiap render supaya listener paste selalu memanggil versi
  // terbaru — lihat catatan di deklarasi addImagesRef.
  addImagesRef.current = addImageFiles;

  // ── Spreadsheet ─────────────────────────────────────────────────────────

  const loadSheetFile = async (file) => {
    setError(null);
    try {
      const buf = await file.arrayBuffer();
      const book = XLSX.read(buf, { type: 'array' });
      setWb(book);
      const { best, scores } = pickBestSheet(book);
      applySheet(book, best, file.name, scores);
    } catch (e) {
      setError(`Gagal membaca ${file.name}: ${e.message}`);
    }
  };

  const applySheet = (book, name, fileName, scores) => {
    const a = analyzeSheet(book, name);
    setSheet({
      fileName: fileName || sheet?.fileName || '(berkas)',
      sheetNames: book.SheetNames, active: name,
      headers: a.headers, body: a.body,
      // Skor seluruh lembar dipertahankan supaya dropdown tetap bisa
      // menampilkannya setelah pengguna berpindah lembar sendiri.
      scores: scores || sheet?.scores || null,
    });
    setMapping(a.mapping);
    resetResults();
  };

  const sheetCandidates = useMemo(() => {
    if (!sheet) return [];
    const out = [];
    sheet.body.forEach((row) => {
      if (!row.some(c => String(c || '').trim() !== '')) return;
      // Baris yang tidak membawa SATU PUN field spesifikasi dilewati. Catatan
      // penjelas di bawah tabel jatuh ke kolom pertama — yang di lembar
      // Re-import adalah kolom `kind` — sehingga baris prosa lolos sebagai
      // kandidat ber-kind tak dikenal dan muncul sebagai baris merah tanpa
      // sebab. Terbukti pada uji round-trip: baris peringatan "usage_key
      // kosong" itu sendiri menjadi kandidat.
      //
      // `kind` dan `source_text` DIKECUALIKAN dari pemeriksaan ini, keduanya
      // dengan alasan berbeda: source_text tidak pernah menentukan apa pun,
      // dan kind adalah kolom pertama sehingga selalu tertimpa prosa. Baris
      // yang punya field lain tetap DIPERTAHANKAN meski kind-nya salah tulis —
      // ia akan tampil merah dengan alasan jelas, bukan hilang tanpa jejak.
      // Menjatuhkan baris berisi data lebih buruk daripada menampilkan merah.
      const carriesSpec = Object.entries(mapping).some(([f, idx]) => (
        f !== 'source_text' && f !== 'kind' && cell(row, idx) !== undefined));
      if (!carriesSpec) return;
      const kindRaw = (cell(row, mapping.kind) || fixedKind || '').toLowerCase();
      const kind = ['ec2', 'rds', 'opensearch', 'usage'].includes(kindRaw) ? kindRaw : null;
      const c = {
        kind,
        source_text: cell(row, mapping.source_text)
          // Tanpa kolom source_text, seluruh baris dirangkai jadi kutipan.
          // Baris tanpa jejak sumber membuat panel review kehilangan gunanya.
          || row.map(v => String(v ?? '').trim()).filter(Boolean).join(' | ').slice(0, 240) || null,
      };
      ['instance_type', 'os', 'tenancy', 'pre_installed_sw', 'db_engine',
       'db_edition', 'deployment_option', 'license_model', 'usage_key'].forEach(f => {
        const v = cell(row, mapping[f]);
        if (v !== undefined) c[f] = v;
      });
      const amount = numOrNull(mapping.amount !== undefined ? row[mapping.amount] : null);
      if (amount !== null) c.amount = amount;
      const qty = numOrNull(mapping.qty !== undefined ? row[mapping.qty] : null);
      if (qty !== null) c.qty = qty;
      out.push(c);
    });
    return out;
  }, [sheet, mapping, fixedKind]);

  // ── Kirim ───────────────────────────────────────────────────────────────

  const runExtract = async () => {
    if (!images.length) return;
    setBusy(true); setError(null); resetResults();
    try {
      const res = await api.post('/api/aws/pricing/import/extract', {
        region,
        images: images.map(im => ({ name: im.name, mimetype: im.mimetype, data_base64: im.data_base64 })),
      });
      const d = res.data || {};
      setCands(d.candidates || []);
      setRows(d.rows || []);
      setMeta({
        source: 'image', model: d.model, images: d.images, usage: d.usage,
        summary: d.summary, region: d.region,
        rejected_files: d.rejected_files || [], stripped_fields: d.stripped_fields || [],
      });
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const runValidateSheet = async () => {
    const cs = sheetCandidates;
    if (!cs.length) { setError('Tidak ada baris berisi pada lembar ini.'); return; }
    if (cs.length > 100) { setError(`${cs.length} baris melewati batas 100 kandidat per permintaan. Pecah berkasnya.`); return; }
    setBusy(true); setError(null); resetResults();
    try {
      const res = await api.post('/api/aws/pricing/import/validate', { region, candidates: cs });
      const d = res.data || {};
      setCands(cs);
      setRows(d.rows || []);
      setMeta({ source: 'sheet', summary: d.summary, region: d.region, file: sheet?.fileName, sheet: sheet?.active });
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  // Revalidasi SATU baris setelah pengguna mengisi field `needs`.
  // Kandidatnya ditulis ulang lalu dikirim ke /import/validate — spec tidak
  // pernah ditambal di klien. Lihat catatan kaskade RDS di kepala berkas.
  const revalidateRow = async (i, patch) => {
    const nextCand = { ...(cands[i] || {}), ...patch };
    // Mengubah dimensi hulu membuat pilihan hilir tidak sah lagi. Katalog
    // yang menentukan ulang, jadi nilai lama dibuang di sini.
    if (patch.db_engine !== undefined) {
      delete nextCand.deployment_option; delete nextCand.db_edition; delete nextCand.license_model;
    }
    if (patch.deployment_option !== undefined) {
      delete nextCand.db_edition; delete nextCand.license_model;
    }
    setCands(prev => prev.map((c, k) => (k === i ? nextCand : c)));
    setRowBusy(prev => ({ ...prev, [i]: true }));
    try {
      const res = await api.post('/api/aws/pricing/import/validate', {
        region, candidates: [nextCand],
      });
      const r = res.data?.rows?.[0];
      if (r) setRows(prev => prev.map((x, k) => (k === i ? r : x)));
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setRowBusy(prev => { const n = { ...prev }; delete n[i]; return n; });
    }
  };

  const dropRow = (i) => {
    setCands(prev => prev.filter((_, k) => k !== i));
    setRows(prev => prev.filter((_, k) => k !== i));
  };

  // ── Ke cart ─────────────────────────────────────────────────────────────

  // `spec` dari validator BUKAN spec /calculator/rates: ia membawa `qty` dan
  // (untuk usage) `amount`, yang tidak dikenal endpoint itu. Keduanya dilepas
  // di sini dan dibawa terpisah — `amount` menjadi volumeGb pada baris cart.
  // `cand` diperlukan di samping `r`: usage_key adalah masukan yang dibaca dari
  // sumber, bukan hasil resolusi, jadi validator tidak mengembalikannya di
  // `spec`. Tanpa ini baris usage kehilangan asal-usulnya begitu masuk cart dan
  // tidak bisa ditulis ke lembar Re-import ekspor.
  const toCartLine = (r, cand) => {
    const s = r.spec || {};
    const kind = s.kind || r.kind;
    const spec = { kind, region: s.region || region };
    if (kind === 'usage') {
      spec.service_code = s.service_code;
      spec.usage_type = s.usage_type;
      // Dipertahankan apa adanya, termasuk null. Lihat isReady().
      spec.operation = s.operation ?? null;
    } else {
      spec.instance_type = s.instance_type;
      if (kind === 'ec2') {
        spec.os = s.os; spec.tenancy = s.tenancy;
        spec.pre_installed_sw = s.pre_installed_sw || 'NA';
      }
      if (kind === 'rds') {
        spec.db_engine = s.db_engine; spec.db_edition = s.db_edition || '';
        spec.deployment_option = s.deployment_option; spec.license_model = s.license_model || '';
      }
    }
    const label = kind === 'ec2'
      ? `${s.instance_type} · ${s.os}${s.pre_installed_sw && s.pre_installed_sw !== 'NA' ? ` · ${s.pre_installed_sw}` : ''}`
      : kind === 'rds'
      ? `${s.instance_type} · ${s.db_engine}${s.db_edition ? ` ${s.db_edition}` : ''} · ${s.deployment_option}`
      : kind === 'opensearch'
      ? `${s.instance_type} · OpenSearch`
      : `${s.service_code} · ${s.usage_type}${s.operation ? ` · ${s.operation}` : ''}`;
    return {
      spec, label,
      usage_key: kind === 'usage' ? ((cand && cand.usage_key) || null) : null,
      qty: Number(s.qty) || 1,
      volumeGb: kind === 'usage' ? (Number(s.amount) || 0) : 0,
      source_text: r.source_text || null,
      defaults: r.defaults || [],
      region_note: r.region_note || null,
    };
  };

  // Indeks dipertahankan, bukan hanya barisnya: cands dan rows sejajar indeks,
  // dan toCartLine membutuhkan pasangannya.
  const readyIdx = rows.map((_, i) => i).filter(i => isReady(rows[i]));

  const sendToCart = () => {
    if (!readyIdx.length) return;
    // Region diambil dari baris pertama, bukan dari dropdown: baris Route 53
    // global diresolusi di region LAIN dan itu dilaporkan lewat region_note.
    // Spec masing-masing baris tetap membawa regionnya sendiri.
    onSendToCart?.({ region, lines: readyIdx.map(i => toCartLine(rows[i], cands[i])) });
  };

  // ── Render ──────────────────────────────────────────────────────────────

  const summary = meta?.summary;
  const mappedCount = Object.keys(mapping).length;
  // Lembar lain yang skornya lebih baik dari yang sedang dibuka. Ditawarkan
  // hanya bila memang ada — saran yang menunjuk ke lembar sama-sama buruk lebih
  // menyesatkan daripada tidak ada saran.
  const bestOtherSheet = (() => {
    if (!sheet?.scores) return null;
    let name = null, sc = mappedCount;
    Object.entries(sheet.scores).forEach(([n, v]) => {
      if (n !== sheet.active && v > sc) { name = n; sc = v; }
    });
    return name;
  })();

  return (
    <div className="space-y-4">
      {/* Sumber */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="block text-xs text-gray-500 mb-1">{tt('awsp.import.region', 'Region')}</span>
            <select
              value={region}
              onChange={e => { setRegion(e.target.value); resetResults(); }}
              className="px-2 py-1.5 rounded border border-gray-300 text-sm bg-white"
            >
              {REGIONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </label>
          <p className="text-xs text-gray-500 flex-1 min-w-[16rem] flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
            {tt('awsp.import.hint',
              'Images are read by the AI model. Spreadsheets are parsed in the browser — no model, no token cost.')}
          </p>
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          {/* Gambar */}
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => {
              e.preventDefault(); setDragging(false);
              const fs = [...(e.dataTransfer?.files || [])];
              const sheets = fs.filter(f => SHEET_EXT.test(f.name));
              const imgs = fs.filter(f => !SHEET_EXT.test(f.name));
              if (sheets.length) loadSheetFile(sheets[0]);
              if (imgs.length) addImageFiles(imgs);
            }}
            className={`rounded-lg border-2 border-dashed p-4 text-center ${
              dragging ? 'border-emerald-500 bg-emerald-50' : 'border-gray-300 bg-gray-50'}`}
          >
            <Upload className="w-6 h-6 mx-auto text-gray-400" />
            <p className="text-sm text-gray-600 mt-2">
              {tt('awsp.import.drop', 'Drag files here, paste a screenshot with Ctrl+V, or')}{' '}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="text-emerald-700 underline"
              >
                {tt('awsp.import.browse', 'browse')}
              </button>
            </p>
            <p className="text-xs text-gray-400 mt-1">
              PNG · JPEG · GIF · WEBP (max {MAX_IMAGES} × 4 MB) · XLSX · XLS · CSV
            </p>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".png,.jpg,.jpeg,.gif,.webp,.xlsx,.xlsm,.xls,.csv"
              className="hidden"
              onChange={e => {
                const fs = [...(e.target.files || [])];
                const sheets = fs.filter(f => SHEET_EXT.test(f.name));
                const imgs = fs.filter(f => !SHEET_EXT.test(f.name));
                if (sheets.length) loadSheetFile(sheets[0]);
                if (imgs.length) addImageFiles(imgs);
                e.target.value = '';
              }}
            />

            {!!images.length && (
              <ul className="mt-3 space-y-1 text-left">
                {images.map((im, i) => (
                  <li key={`${im.name}-${i}`} className="flex items-center gap-2 text-xs bg-white rounded border border-gray-200 px-2 py-1">
                    <ImageIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="truncate flex-1" title={im.name}>{im.name}</span>
                    <span className="text-gray-400 shrink-0">{(im.size / 1024).toFixed(0)} KB</span>
                    <button
                      type="button"
                      onClick={() => setImages(prev => prev.filter((_, k) => k !== i))}
                      className="text-gray-400 hover:text-red-600 shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <button
              type="button"
              disabled={!images.length || busy}
              onClick={runExtract}
              className="mt-3 px-3 py-1.5 rounded bg-emerald-600 text-white text-sm disabled:bg-gray-300 inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {tt('awsp.import.readImages', 'Read images with AI')}
            </button>
          </div>

          {/* Spreadsheet */}
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
              {tt('awsp.import.sheetTitle', 'Spreadsheet (no AI)')}
            </div>

            {!sheet && (
              <p className="text-xs text-gray-400 mt-2">
                {tt('awsp.import.sheetEmpty', 'Drop an XLSX or CSV file to map its columns.')}
              </p>
            )}

            {sheet && (
              <div className="mt-2 space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="truncate max-w-[12rem] text-gray-600" title={sheet.fileName}>{sheet.fileName}</span>
                  <select
                    value={sheet.active}
                    onChange={e => wb && applySheet(wb, e.target.value, sheet.fileName)}
                    className="px-1.5 py-1 rounded border border-gray-300 bg-white"
                  >
                    {/* Skor tiap lembar ditulis di labelnya. Tanpa ini "103 data
                        rows · 9 columns" terbaca seperti kabar baik padahal
                        lembarnya salah. */}
                    {sheet.sheetNames.map(n => (
                      <option key={n} value={n}>
                        {n}{sheet.scores && sheet.scores[n] !== undefined ? ` · ${sheet.scores[n]} mapped` : ''}
                      </option>
                    ))}
                  </select>
                  <span className="text-gray-400">
                    {sheet.body.length} {tt('awsp.import.dataRows', 'data rows')} · {sheet.headers.length} {tt('awsp.import.columns', 'columns')}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded ${
                    mappedCount >= LOW_MAPPING ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`}>
                    {mappedCount} {tt('awsp.import.mapped', 'mapped')}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSheet(null); setWb(null); setMapping({}); resetResults(); }}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                {mappedCount < LOW_MAPPING && (
                  <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                    {tt('awsp.import.lowMapping',
                      'Almost no columns were recognised on this sheet. Validating it will mostly produce red rows.')}
                    {bestOtherSheet && (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={() => wb && applySheet(wb, bestOtherSheet, sheet.fileName)}
                          className="underline font-medium"
                        >
                          {tt('awsp.import.trySheet', 'Try')} “{bestOtherSheet}” ({sheet.scores[bestOtherSheet]} {tt('awsp.import.mapped', 'mapped')})
                        </button>
                      </>
                    )}
                  </div>
                )}

                <label className="block">
                  <span className="block text-xs text-gray-500 mb-1">
                    {tt('awsp.import.fixedKind', 'Row kind when no column maps to it')}
                  </span>
                  <select
                    value={fixedKind}
                    onChange={e => setFixedKind(e.target.value)}
                    className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white"
                  >
                    <option value="">— {tt('awsp.import.perRow', 'from column only')} —</option>
                    <option value="ec2">ec2</option>
                    <option value="rds">rds</option>
                    <option value="opensearch">opensearch</option>
                    <option value="usage">usage</option>
                  </select>
                </label>

                <div className="grid gap-1.5 sm:grid-cols-2 max-h-64 overflow-y-auto pr-1">
                  {SHEET_FIELDS.map(f => (
                    <label key={f.key} className="block">
                      <span className="block text-[11px] text-gray-500 truncate" title={f.label}>{f.label}</span>
                      <SearchableSelect
                        value={mapping[f.key] === undefined ? '' : String(mapping[f.key])}
                        onChange={v => setMapping(prev => {
                          const n = { ...prev };
                          if (v === '') delete n[f.key]; else n[f.key] = Number(v);
                          return n;
                        })}
                        options={sheet.headers.map((h, i) => ({
                          value: String(i),
                          label: h || `(kolom ${i + 1})`,
                          sub: String(sheet.body[0]?.[i] ?? '').slice(0, 40) || undefined,
                        }))}
                        allLabel={`— ${tt('awsp.import.unmapped', 'not mapped')} —`}
                        placeholder={tt('awsp.import.pickColumn', 'pick a column')}
                      />
                    </label>
                  ))}
                </div>

                <button
                  type="button"
                  disabled={busy || !sheetCandidates.length}
                  onClick={runValidateSheet}
                  className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm disabled:bg-gray-300 inline-flex items-center gap-2"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {tt('awsp.import.validateSheet', 'Validate')} ({sheetCandidates.length}) · {mappedCount} {tt('awsp.import.mapped', 'mapped')}
                </button>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">{notice}</div>
        )}
      </div>

      {/* Ringkasan */}
      {summary && (
        <div className="rounded-xl border border-gray-200 bg-white p-3 flex flex-wrap items-center gap-3 text-sm">
          <span className="font-medium text-gray-700">
            {summary.total} {tt('awsp.import.candidates', 'candidates')}
          </span>
          <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs">{summary.green} green</span>
          <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 text-xs">{summary.yellow} yellow</span>
          <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs">{summary.red} red</span>
          {meta?.model && (
            <span className="text-xs text-gray-500">
              {meta.model}{meta.usage?.totalTokens ? ` · ${meta.usage.totalTokens} tokens` : ''}
              {meta.images ? ` · ${meta.images} img` : ''}
            </span>
          )}
          {meta?.file && <span className="text-xs text-gray-500">{meta.file} · {meta.sheet}</span>}
          <div className="flex-1" />
          <button
            type="button"
            disabled={!readyIdx.length}
            onClick={sendToCart}
            className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm disabled:bg-gray-300 inline-flex items-center gap-2"
          >
            <ShoppingCart className="w-4 h-4" />
            {tt('awsp.import.toCart', 'Send to cart')} ({readyIdx.length})
          </button>
        </div>
      )}

      {/* Peringatan model melampaui wewenang. stripped_fields kosong adalah
          keadaan normal; menampilkannya hanya saat TIDAK kosong membuat
          kemunculannya berarti sesuatu. */}
      {!!meta?.stripped_fields?.length && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          {tt('awsp.import.stripped', 'Fields discarded from the model output (resolved from the catalog instead):')}{' '}
          <span className="font-mono">{meta.stripped_fields.join(', ')}</span>
        </div>
      )}
      {!!meta?.rejected_files?.length && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          {tt('awsp.import.rejectedFiles', 'Files rejected by the server:')}{' '}
          {meta.rejected_files.map(f => (typeof f === 'string' ? f : `${f.name}: ${f.reason}`)).join(' · ')}
        </div>
      )}

      {!!readyIdx.length && readyIdx.length !== rows.length && (
        <div className="rounded-lg border border-brand-200 bg-brand-50 p-3 text-xs text-brand-900 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {tt('awsp.import.replaceWarn',
              'Sending to cart REPLACES the current cart, the same way loading a saved estimate does. Rows still missing a required field are left behind.')}
          </span>
        </div>
      )}

      {/* Panel review */}
      {rows.map((r, i) => {
        const st = STATUS_STYLE[r.status] || STATUS_STYLE.red;
        const StIcon = st.Icon;
        const kind = r.spec?.kind || r.kind || '?';
        const fields = SPEC_FIELDS[kind] || [];
        const cand = cands[i] || {};
        const ready = isReady(r);
        return (
          <div key={i} className={`rounded-xl border p-3 ${st.box}`}>
            <div className="flex items-start gap-2">
              <StIcon className={`w-4 h-4 mt-0.5 shrink-0 ${st.text}`} />
              <span className={`text-xs px-1.5 py-0.5 rounded text-white shrink-0 ${st.chip}`}>{r.status}</span>
              <span className="text-xs text-gray-500 shrink-0">#{i + 1} · {kind}</span>
              {r.label && <span className="text-xs text-gray-600 truncate">{r.label}</span>}
              <div className="flex-1" />
              {rowBusy[i] && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
              {ready && <span className="text-xs text-emerald-700">{tt('awsp.import.ready', 'ready')}</span>}
              <button
                type="button"
                onClick={() => dropRow(i)}
                title={tt('awsp.import.dropRow', 'Discard this row')}
                className="text-gray-400 hover:text-red-600"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* SUMBER DI KIRI, HASIL DI KANAN, selalu terlihat. Ini satu-satunya
                tempat salah baca yang menghasilkan baris hijau bisa tertangkap. */}
            <div className="mt-2 grid gap-3 md:grid-cols-2">
              <div className="rounded-lg bg-white/70 border border-gray-200 p-2">
                <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
                  {tt('awsp.import.source', 'Read from source')}
                </div>
                <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                  {r.source_text || <span className="text-gray-400">—</span>}
                </p>
                {/* Bacaan mentah model di bawah kutipannya: field yang dibaca
                    tetapi TIDAK tampil di spec (mis. amount_range) hanya
                    terlihat di sini. */}
                <dl className="mt-1.5 text-[11px] text-gray-500 grid grid-cols-[auto_1fr] gap-x-2">
                  {Object.entries(cand).filter(([k]) => k !== 'source_text')
                    .map(([k, v]) => (
                      <React.Fragment key={k}>
                        <dt className="font-mono">{k}</dt>
                        <dd className="truncate">{Array.isArray(v) ? v.join(' – ') : String(v)}</dd>
                      </React.Fragment>
                    ))}
                </dl>
              </div>

              <div className="rounded-lg bg-white/70 border border-gray-200 p-2">
                <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
                  {tt('awsp.import.resolved', 'Resolved against catalog')}
                </div>
                {r.status === 'red' ? (
                  <p className="text-sm text-red-800">{r.reason}</p>
                ) : (
                  <>
                    <dl className="text-xs grid grid-cols-[9rem_1fr] gap-x-2 gap-y-0.5">
                      {fields.map(f => {
                        const v = r.spec?.[f];
                        const guessed = (r.defaults || []).includes(f)
                          || (f === 'instance_type' && (r.defaults || []).includes('instance_type_suffix'));
                        return (
                          <React.Fragment key={f}>
                            <dt className="font-mono text-gray-500">{f}</dt>
                            <dd className={`truncate ${guessed ? 'text-amber-800' : 'text-gray-800'}`}>
                              {v === null || v === undefined || v === ''
                                ? <span className="text-gray-400">—</span>
                                : String(v)}
                              {guessed && (
                                <span className="ml-1 text-[10px] px-1 rounded bg-amber-200 text-amber-900">
                                  {tt('awsp.import.guessed', 'default')}
                                </span>
                              )}
                            </dd>
                          </React.Fragment>
                        );
                      })}
                    </dl>
                    {r.resolved && (
                      <p className="mt-1 text-[11px] text-gray-500 font-mono truncate"
                         title={`${r.resolved.sku} · ${r.resolved.usage_type} · ${r.resolved.operation || ''}`}>
                        {r.resolved.usage_type} · {r.resolved.operation || '—'}
                        {r.resolved.vcpu ? ` · ${r.resolved.vcpu} vCPU` : ''}
                        {r.resolved.memory_gib ? ` · ${r.resolved.memory_gib} GiB` : ''}
                      </p>
                    )}
                    {r.region_note && (
                      <p className="mt-1 text-[11px] text-brand-800 bg-brand-50 rounded px-1.5 py-1">{r.region_note}</p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Field wajib. Baris tidak bisa masuk cart sampai semuanya terisi. */}
            {!!(r.needs || []).length && (
              <div className="mt-2 rounded-lg border border-amber-300 bg-white/70 p-2">
                <div className="text-[11px] uppercase tracking-wide text-amber-800 mb-1.5">
                  {tt('awsp.import.needs', 'Required — fill these in')}
                </div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {r.needs.map(f => {
                    const opts = r.options?.[f];
                    if (f === 'qty' || (f === 'amount' && !Array.isArray(opts))) {
                      return (
                        <label key={f} className="block">
                          <span className="block text-[11px] text-gray-500 mb-1 font-mono">{f}</span>
                          <input
                            type="number" min="0" step="any"
                            defaultValue=""
                            onBlur={e => {
                              const v = numOrNull(e.target.value);
                              if (v !== null) revalidateRow(i, { [f]: v });
                            }}
                            className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm"
                            placeholder={r.unit_hint || ''}
                          />
                        </label>
                      );
                    }
                    if (f === 'amount' && Array.isArray(opts)) {
                      // Rentang dari sumber ('~1-2 TB'). Ekstraktor sengaja tidak
                      // memilih sendiri; kedua ujungnya ditawarkan apa adanya
                      // beserta kotak isian bebas.
                      return (
                        <div key={f}>
                          <span className="block text-[11px] text-gray-500 mb-1 font-mono">
                            {f}{r.unit_hint ? ` (${r.unit_hint})` : ''}
                          </span>
                          <div className="flex gap-1.5">
                            {opts.map(v => (
                              <button
                                key={v} type="button"
                                onClick={() => revalidateRow(i, { amount: Number(v) })}
                                className="px-2 py-1.5 rounded border border-amber-400 bg-amber-100 text-amber-900 text-sm"
                              >
                                {v}
                              </button>
                            ))}
                            <input
                              type="number" min="0" step="any" defaultValue=""
                              onBlur={e => {
                                const v = numOrNull(e.target.value);
                                if (v !== null) revalidateRow(i, { amount: v });
                              }}
                              className="flex-1 min-w-0 px-2 py-1.5 rounded border border-gray-300 text-sm"
                            />
                          </div>
                        </div>
                      );
                    }
                    return (
                      <label key={f} className="block">
                        <span className="block text-[11px] text-gray-500 mb-1 font-mono">{f}</span>
                        <SearchableSelect
                          value={cand[f] || ''}
                          onChange={v => { if (v) revalidateRow(i, { [f]: v }); }}
                          options={(opts || []).map(o => (typeof o === 'string'
                            ? { value: o, label: o }
                            : { value: o.value ?? '', label: o.label || o.value || '(kosong)' }))}
                          placeholder={tt('awsp.import.choose', 'choose')}
                          wide={f === 'operation'}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {!rows.length && !busy && (
        <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400">
          {tt('awsp.import.noRows', 'No rows yet. Upload an image or a spreadsheet above.')}
        </div>
      )}

      {/* Yang sengaja tidak didukung, ditampilkan supaya penolakan tidak tampak
          seperti kegagalan. */}
      {!!unsupported.length && (
        <details className="rounded-xl border border-gray-200 bg-white p-3">
          <summary className="text-sm text-gray-600 cursor-pointer">
            {tt('awsp.import.unsupportedTitle', 'Deliberately unsupported line types')} ({unsupported.length})
          </summary>
          <ul className="mt-2 space-y-1.5 text-xs text-gray-600">
            {unsupported.map(u => (
              <li key={u.key}>
                <span className="font-mono text-gray-800">{u.key}</span> — {u.reason}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-gray-400">
            {usageKeys.length} {tt('awsp.import.knownKeys', 'usage keys are recognised')}.
          </p>
        </details>
      )}
    </div>
  );
}
